import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert
} from '@mui/material';
import { AvatarCreator, AvatarCreatorConfig, AvatarExportedEvent } from '@readyplayerme/react-avatar-creator';

interface AvatarCreatorModalProps {
  open: boolean;
  onClose: () => void;
  onAvatarCreated: (avatarUrl: string) => void;
  currentAvatarUrl?: string;
}

const AvatarCreatorModal: React.FC<AvatarCreatorModalProps> = ({
  open,
  onClose,
  onAvatarCreated,
  currentAvatarUrl
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Ready Player Me configuration
  const config: AvatarCreatorConfig = {
    clearCache: false, // Keep previous avatar loaded
    bodyType: 'fullbody',
    quickStart: false,
    language: 'en',
  };

  const style = { 
    width: '100%', 
    height: '600px', 
    border: 'none',
    borderRadius: '8px'
  };

  const handleAvatarExported = (event: AvatarExportedEvent) => {
    try {
      setIsLoading(true);
      setError(null);
      
      console.log('Avatar created:', event.data.url);
      
      // Call the parent callback with the new avatar URL
      onAvatarCreated(event.data.url);
      
      // Close the modal after successful creation
      onClose();
    } catch (err) {
      console.error('Error handling avatar export:', err);
      setError('Failed to save avatar. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: '80vh',
          maxHeight: '800px'
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {currentAvatarUrl ? 'Edit Your Avatar' : 'Create Your Avatar'}
          </Typography>
          <Button
            onClick={handleClose}
            disabled={isLoading}
            color="inherit"
            size="small"
          >
            Close
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, position: 'relative' }}>
        {error && (
          <Alert severity="error" sx={{ m: 2, mb: 0 }}>
            {error}
          </Alert>
        )}

        {isLoading && (
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            display="flex"
            alignItems="center"
            justifyContent="center"
            bgcolor="rgba(255, 255, 255, 0.8)"
            zIndex={1000}
          >
            <Box textAlign="center">
              <CircularProgress size={40} />
              <Typography variant="body2" sx={{ mt: 1 }}>
                Saving your avatar...
              </Typography>
            </Box>
          </Box>
        )}

        <Box sx={{ height: '100%', minHeight: '600px' }}>
          <AvatarCreator
            subdomain="wakarunning" // Your actual subdomain
            config={config}
            style={style}
            onAvatarExported={handleAvatarExported}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={handleClose}
          disabled={isLoading}
          variant="outlined"
        >
          Cancel
        </Button>
        <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1, textAlign: 'center' }}>
          Customize your avatar and click "Save" in the creator to continue
        </Typography>
      </DialogActions>
    </Dialog>
  );
};

export default AvatarCreatorModal;
